import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Switch, Typography, FormControlLabel } from '@mui/material';
import { HelpCircle } from 'lucide-react';
import DirectoryBase from '../CreateDirectory/DirectoryBase';
import HoverTooltip from '../../common_components/hoverOverlay/HoverTooltip';
import { fetchOpportunitiesThunk, deleteOpportunityThunk, selectOpportunities, selectOpportunityLoading, selectOpportunityTotal, selectLastUpdated, selectOpportunityTags, selectDropdownOptions, } from '../../redux/opportunitySlice';
import UploadDirectoryDrawer from '../CreateDirectory/components/UploadDirectoryDrawer';
import AddNewContentOpportunityDrawer from './components/AddNewContentOpportunityDrawer';
import exportContentOpportunity from './exportContentOpportunity';
import { getCreatorDirectoryRenderers } from '../CreateDirectory/components/DirectoryCells';
// ✅ Custom Tooltip Content
const CustomTooltipContent = () => (_jsxs(Box, { sx: { p: 1.5, maxWidth: 280 }, children: [_jsx(Typography, { className: 'all-Font-Sizes', sx: { mb: 1, fontWeight: 600, color: '#fff', fontSize: '14px' }, children: "Filter Content Opportunities" }), _jsxs(Typography, { className: 'all-Font-Sizes', sx: { mb: 1.5, lineHeight: 1.6, color: '#EAECF0', fontSize: '13px' }, children: ["Toggle this switch to filter the directory. When ", _jsx("span", { style: { color: '#ff008e', fontWeight: 600 }, children: "Active" }), ", it shows only ", _jsx("b", { children: "Non-Paid" }), " opportunities. When inactive, it shows only ", _jsx("b", { children: "Paid" }), " opportunities."] })] }));
const CONFIG = {
    MODULE_KEY: 'content_opportunities',
    ITEMS_PER_PAGE: 20,
    ICONS: {
        category: '/assets/widgetadd.svg',
        tag: '/assets/tag.svg',
        type: '/assets/bill.svg',
    },
};
const TYPE_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'creator', label: 'Creator' },
    { value: 'publisher', label: 'Publisher' },
    { value: 'platform_tool', label: 'Platform/Tool' },
];
const COLUMNS = [
    { id: 'category', label: 'Category', minWidth: 130, sortable: true, draggable: false },
    { id: 'type', label: 'Type', minWidth: 100, sortable: true },
    { id: 'publisher', label: 'Name', tooltip: 'creator/publisher/network', minWidth: 180, sortable: true },
    // { id: 'contentType', label: 'Content Type', minWidth: 120, sortable: true },
    { id: 'stats', label: 'Stats', minWidth: 100, sortable: true },
    { id: 'requirement', label: 'Requirement', minWidth: 200, sortable: false },
    { id: 'tags', label: 'Tags', minWidth: 180, sortable: true },
    { id: 'primary_lead_name', label: 'Primary Lead', minWidth: 150, sortable: true },
    { id: 'date_added', label: 'Date Added', minWidth: 130, sortable: true },
    { id: 'action', label: 'Action', minWidth: 120, sortable: false, draggable: false },
];
const ContentOpportunities = () => {
    const dispatch = useDispatch();
    const dropdownOptions = useSelector(selectDropdownOptions);
    const tags = useSelector(selectOpportunityTags);
    const [showOnlyNonPaid, setShowOnlyNonPaid] = React.useState(false);
    const renderCell = useCallback((row, column, onEdit, onSuccess) => {
        const renderers = getCreatorDirectoryRenderers(row, onEdit, onSuccess, dispatch);
        const wrap = (node) => (_jsx("div", { className: "all-Font-Sizes", style: { textTransform: 'capitalize', color: '#344054', fontSize: '14px' }, children: node }));
        return renderers[column.id]?.() ?? wrap(row[column.id] || '-');
    }, [dispatch]);
    const filtersConfig = useMemo(() => [
        {
            key: 'category',
            icon: CONFIG.ICONS.category,
            label: 'Category',
            options: [{ value: 'all', label: 'All' }, ...(dropdownOptions?.map((c) => ({ value: c.value, label: c.label })) || [])]
        },
        {
            key: 'tags',
            icon: CONFIG.ICONS.tag,
            label: 'Tags',
            options: [{ value: 'all', label: 'All' }, ...(tags?.map((t) => ({ value: t, label: t })) || [])]
        },
        {
            key: 'type',
            icon: CONFIG.ICONS.type,
            label: 'Type',
            options: TYPE_OPTIONS
        },
    ], [dropdownOptions, tags]);
    const renderHeaderExtra = useCallback(() => (_jsxs(Box, { sx: {
            display: 'flex',
            alignItems: 'center',
            border: "1px solid #ddd",
            padding: "0px 8px",
            borderRadius: "8px",
            bgcolor: '#fff',
            height: '42px',
            transition: 'all 0.2s',
            '&:hover': {
                borderColor: '#ff008e',
            }
        }, children: [_jsx(Box, { className: "compare-toggle-container", children: _jsx(FormControlLabel, { control: _jsx(Switch, { checked: showOnlyNonPaid, onChange: (e) => setShowOnlyNonPaid(e.target.checked), className: "compare-switch", sx: {
                            '&.Mui-checked': { color: '#ff008e' },
                            '&.Mui-checked + .MuiSwitch-track': { backgroundColor: '#ff008e' }
                        } }), label: "Non-Paid Only", className: "compare-label all-Font-Sizes", sx: {
                        margin: 0,
                        '.MuiTypography-root': {
                            fontWeight: 500,
                            fontSize: '13px',
                            whiteSpace: 'nowrap',
                            color: '#344054'
                        }
                    } }) }), _jsx(Box, { sx: { display: 'flex', alignItems: 'center' }, children: _jsx(HoverTooltip, { title: _jsx(CustomTooltipContent, {}), arrow: true, placement: "bottom", children: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'help' }, children: [_jsx(HelpCircle, { size: 18, color: "#667085" }), _jsx(Typography, { sx: { fontWeight: 500, fontSize: '13px', color: '#667085', whiteSpace: 'nowrap' }, children: "Filter Info" })] }) }) })] })), [showOnlyNonPaid]);
    return (_jsx(DirectoryBase, { moduleKey: CONFIG.MODULE_KEY, pageTitle: "Content Opportunities", addNewButtonLabel: "Add New Opportunity", uploadButtonLabel: "Upload Opportunity", fetchThunk: fetchOpportunitiesThunk, deleteThunk: deleteOpportunityThunk, exportFn: exportContentOpportunity, renderCell: renderCell, columns: COLUMNS, filtersConfig: filtersConfig, AddNewDrawer: AddNewContentOpportunityDrawer, UploadDrawer: UploadDirectoryDrawer, renderHeaderExtra: renderHeaderExtra, extraParams: useMemo(() => ({
            // Active toggle → Non-Paid only; inactive → Paid only.
            contentType: showOnlyNonPaid ? 'Non-Paid' : 'Paid'
        }), [showOnlyNonPaid]), selectors: useMemo(() => ({
            items: selectOpportunities,
            loading: selectOpportunityLoading,
            total: selectOpportunityTotal,
            lastUpdated: selectLastUpdated,
            tags: selectOpportunityTags,
        }), []) }));
};
export default ContentOpportunities;
